import {
  FilterParams,
  KeysToCamelCase,
  MetricDistributionRange,
  MetricDistributionBucket,
  MetricDistributionPercentiles,
  MetricDistributionQuartiles,
  MetricValueMode,
} from '@statsbomb/parachute-types';
import i18next from 'i18next';
import { roundToFixed } from '@/components/modal/CreateRadarTemplateModal/metricLimitsUtils';
import { toDisplayValue } from '@/components/sliders/sliderUtils';
import { ON_DEMAND_METRIC_KEYS, OnDemandIncludeParams } from '@/consts/onDemandMetrics';
import { Nullable } from '@/types/generic';
import { MetricLookupMap, PHYSICAL_SOURCE } from '@/types/metric';
import { DataTableColumn } from '@/types/table';
import { isNullish } from './predicates';
import { camelToSnakeCase, objSnakeToCamel, snakeToCamel } from './queries';

export const hasMetricTranslation = (metricKey: string): boolean =>
  i18next.exists(`${snakeToCamel(metricKey)}.name`, { ns: 'metrics' });

// The maximum amount of steps a metric distribution slider can handle
const METRIC_SLIDER_MAX_STEPS = 200;

/** Type for metric distribution percentiles after formatting, with precision and formattedValue added */
export type FormattedMetricDistributionPercentiles = KeysToCamelCase<MetricDistributionPercentiles> & {
  precision: number;
  step: number;
  percentiles: Array<
    KeysToCamelCase<MetricDistributionPercentiles['percentiles'][number]> & {
      formattedValue: string;
    }
  >;
};

/** Type for metric distribution buckets after formatting, with keys converted to camelCase */
export type FormattedMetricDistributionBucket = Omit<KeysToCamelCase<MetricDistributionBucket>, 'buckets'> & {
  buckets: Array<{ bucketIndex: number; frequency: number }>;
  quartiles: MetricDistributionQuartiles;
};

export const AGGREGATES_COLUMN_PREFIX = 'aggregates.';
const OUTPUT_FILTER_PREFIX = 'output.';
export const FORMAT_RULE_FLOAT_2 = 'float-2';

export const decimalPlaces = (minValue: number, maxValue: number) => {
  const diff = Math.abs(minValue - maxValue);
  if (diff < 1) {
    // When the scale difference is less than one, we want to show 2 significant figures for the numbers on our scale.
    // The Radar component takes decimal places, so we need to convert significant figures to decimal places.
    // To do this, round the diff 2 significant figures, get the length of that, and then subtract 2 to account for the '0.' bit of the rounded diff.
    return diff.toPrecision(2).length - 2;
  }

  if (diff < 10) {
    return 1;
  }

  return 0;
};

export const decimalPlacesFromFormatRule = (formatRule: string): number => {
  if (formatRule === FORMAT_RULE_FLOAT_2) return 2;
  return 0;
};

/**
 * Returns the precision and step for a physical metric based on its DataTableColumn format_rule,
 * or null if the metric is non-physical or has no matching column (caller should fall back to
 * the range-based heuristic).
 */
export const getPhysicalMetricPrecision = (
  metricKey: string,
  metricLookupMap: MetricLookupMap | undefined,
  columns: DataTableColumn[],
): { precision: number; step: number } | null => {
  if (!metricLookupMap) return null;
  const metric = metricLookupMap.get(metricKey);
  if (!metric || metric.source !== PHYSICAL_SOURCE) return null;
  const columnKey = `${AGGREGATES_COLUMN_PREFIX}${snakeToCamel(metric.keyNormalised)}`;
  const column = columns.find(c => c.tableColumn.key === columnKey);
  if (!column) return null;
  const precision = decimalPlacesFromFormatRule(column.tableColumn.formatRule);
  return { precision, step: 10 ** -precision };
};

const separatePrefixFromKey = (columnKey: string) => {
  const splitColumnKey = columnKey.split('.');
  if (splitColumnKey.length === 1) return { prefix: '', key: columnKey };
  const [prefix, key] = splitColumnKey;
  return { prefix: `${prefix}.`, key };
};

export const getMetricKeyFromColumnKey = (columnKey: string) => {
  const { key } = separatePrefixFromKey(columnKey);
  return camelToSnakeCase(key.replace('Per90', ''));
};

export const getDataKeyFromColumnKey = (
  columnKey: string,
  metricLookupMap: MetricLookupMap | undefined,
  mode: MetricValueMode,
  withPrefix = true,
): string => {
  if (!metricLookupMap) {
    return columnKey;
  }

  const { prefix, key: columnMetricKey } = separatePrefixFromKey(columnKey);
  const snakeCaseKey = camelToSnakeCase(columnMetricKey);

  const metricDef = metricLookupMap.get(snakeCaseKey);
  if (!metricDef) {
    return columnKey;
  }

  const metricKey = mode === MetricValueMode.Per90 ? metricDef.keyNormalised : metricDef.key;
  return `${withPrefix ? prefix : ''}${snakeToCamel(metricKey)}`;
};

/**
 * Determines the appropriate step size and precision for a metric distribution slider based on the min and max values.
 *
 * @param minValue the min value of the slider
 * @param maxValue the max value of the slider
 * @param isPercentage whether the metric is a percentage
 */
export const getMetricDistributionPrecision = (minValue: number, maxValue: number, isPercentage: boolean) => {
  if (isPercentage) return { step: 0.1, precision: 1 };

  const rangeToStepsRatio = (maxValue - minValue) / METRIC_SLIDER_MAX_STEPS;

  if (rangeToStepsRatio > 0.1) return { step: 1, precision: 0 };
  if (rangeToStepsRatio > 0.01) return { step: 0.1, precision: 1 };
  if (rangeToStepsRatio > 0.001) return { step: 0.01, precision: 2 };
  return { step: 0.001, precision: 3 };
};

/**
 * Creates a fallback percentiles array with min/max values at specific percentile positions.
 * Used when position-specific distributions are missing but 'all' position data is available.
 *
 * Creates a 101-element array where:
 * - percentiles[5] gets the p0 value (min)
 * - percentiles[95] gets the p100 value (max)
 * - All other percentiles are set to 0
 *
 * This provides sensible default/custom limit initialization while visually indicating
 * that detailed distribution data is not available for this position.
 */
export const createFallbackPercentiles = (
  p0: number,
  p100: number,
  percentage: boolean,
  precision: number,
): FormattedMetricDistributionPercentiles['percentiles'] =>
  Array.from({ length: 101 }, (_, i) => {
    if (i === 5) {
      return {
        percentile: i,
        value: p0,
        formattedValue: roundToFixed(toDisplayValue(p0, percentage), precision),
      };
    }
    if (i === 95) {
      return {
        percentile: i,
        value: p100,
        formattedValue: roundToFixed(toDisplayValue(p100, percentage), precision),
      };
    }
    return { percentile: i, value: 0, formattedValue: '0' };
  });

/**
 * If a metric distribution for a specific position is not found, *and* it's also not found for the 'all' positionGroup
 * distribution, we fall back to this. We don't expect this to happen in practice, but this at least allows the
 * application to continue functioning with a valid distribution structure should something unexpected happen.
 */
const FALLBACK_DISTRIBUTION: FormattedMetricDistributionPercentiles = {
  metricKey: 'fallback',
  metricKeyNormalised: 'fallback',
  percentage: false,
  reverseScale: false,
  precision: 0,
  step: 1,
  percentiles: Array.from({ length: 101 }, (_, i) => ({
    percentile: i,
    value: 0,
    formattedValue: '0',
  })),
};

/**
 * If a metric distribution for a specific position is not found, *and* it's also not found for the 'all' positionGroup
 * distribution, we fall back to this. We don't expect this to happen in practice, but this at least allows the
 * application to continue functioning with a valid distribution structure should something unexpected happen.
 */
const FALLBACK_BUCKET: FormattedMetricDistributionBucket = {
  metricKey: 'fallback',
  metricKeyNormalised: 'fallback',
  percentage: false,
  reverseScale: false,
  min: 0,
  max: 0,
  buckets: [],
  quartiles: { p25: 0, median: 0, p75: 0 },
};

/**
 * @param rawPercentiles the percentile response to format
 * @param getPrecisionOverride optional lookup (by normalised metric key) of a precision and step that should win over
 * the range heuristic, for metrics whose table column dictates its own precision (see `getPhysicalMetricPrecision`)
 */
export const formatMetricDistributions = (
  rawPercentiles: { data: MetricDistributionPercentiles[] | null },
  getPrecisionOverride?: (metricKeyNormalised: string) => { precision: number; step: number } | null,
) => {
  if (!rawPercentiles.data) return [];

  const metricDistributions = rawPercentiles.data.map(objSnakeToCamel);
  return metricDistributions.map(metric => {
    const { percentage, percentiles } = metric;
    const metricP5 = percentiles[5].value;
    const metricP95 = percentiles[95].value;

    const { step, precision } =
      getPrecisionOverride?.(metric.metricKeyNormalised) ??
      getMetricDistributionPrecision(metricP5, metricP95, percentage);

    const formattedPercentiles = percentiles.map(percentile => ({
      ...percentile,
      formattedValue: roundToFixed(toDisplayValue(percentile.value, percentage), precision),
    }));

    return {
      ...metric,
      percentiles: formattedPercentiles,
      precision,
      step,
    };
  });
};

export const getMetricDistributionsParams = (metricDistributions: MetricDistributionRange[]) =>
  metricDistributions.reduce(
    (acc, { name, min, max }) => {
      const gte = isNullish(min) ? {} : { [`${OUTPUT_FILTER_PREFIX}${name}`]: min };
      const lte = isNullish(max) ? {} : { [`${OUTPUT_FILTER_PREFIX}${name}`]: max };

      return {
        lte: { ...acc.lte, ...lte },
        gte: { ...acc.gte, ...gte },
      };
    },
    { gte: {}, lte: {} },
  );

const stripPrefix = (key: string, prefix: string) => (key.startsWith(prefix) ? key.slice(prefix.length) : key);

const toApiMetricKey = (key: string) =>
  camelToSnakeCase(stripPrefix(stripPrefix(key, OUTPUT_FILTER_PREFIX), AGGREGATES_COLUMN_PREFIX));

export const isOnDemandMetricKey = (key: string): boolean => {
  const apiKey = toApiMetricKey(key);
  return ON_DEMAND_METRIC_KEYS.some(onDemandKey => onDemandKey === apiKey);
};

export const getOnDemandIncludeParams = ({
  metricKeys = [],
  orderBy,
  filters,
}: {
  metricKeys?: readonly string[];
  orderBy?: Nullable<Record<string, unknown>>;
  filters?: Nullable<FilterParams>;
}): OnDemandIncludeParams => {
  const apiKeys = new Set(
    [
      ...metricKeys,
      ...Object.keys(orderBy ?? {}),
      ...Object.keys(filters?.gte ?? {}),
      ...Object.keys(filters?.lte ?? {}),
    ].map(toApiMetricKey),
  );
  const include = ON_DEMAND_METRIC_KEYS.filter(key => apiKeys.has(key));

  return include.length ? { include } : {};
};

export const getMetricBaseName = (metric: string) =>
  metric.replace(AGGREGATES_COLUMN_PREFIX, '').replace('_per_90', '').replace('Per90', '');

/**
 * Formats metric distribution bucket data from snake_case to camelCase.
 * Note: buckets array is kept as-is since bucketIndex and frequency are already in camelCase.
 * Returns an empty array if data is null or undefined.
 */
export const formatMetricDistributionBuckets = (result: {
  data: MetricDistributionBucket[] | null;
}): FormattedMetricDistributionBucket[] => {
  if (!result.data) return [];

  return result.data.map(item => ({
    ...objSnakeToCamel(item),
    buckets: item.buckets, // bucketIndex and frequency are already camelCase
    quartiles: item.quartiles,
  }));
};

/**
 * Returns metric distribution data with fallback lookup/generation if position-specific data is missing for the given
 * metric
 */
export const getMetricDistribution = (
  metricKey: string,
  positionSpecific: FormattedMetricDistributionPercentiles[],
  allPositions?: FormattedMetricDistributionPercentiles[],
): FormattedMetricDistributionPercentiles => {
  // Try position-specific first
  const positionDist = positionSpecific.find(d => d.metricKey === metricKey);
  if (positionDist) return positionDist;

  // Fall back to 'all' positions
  if (allPositions) {
    const allDist = allPositions.find(d => d.metricKey === metricKey);
    if (allDist) {
      const p0 = allDist.percentiles[0].value;
      const p100 = allDist.percentiles[100].value;

      // Recompute precision/step based on the actual p0-p100 range, not the p5-p95 range
      // that allDist's precision/step were originally computed from
      const { step, precision } = getMetricDistributionPrecision(p0, p100, allDist.percentage);

      return {
        ...FALLBACK_DISTRIBUTION,
        metricKey,
        metricKeyNormalised: allDist.metricKeyNormalised,
        percentage: allDist.percentage,
        reverseScale: allDist.reverseScale,
        precision,
        step,
        percentiles: createFallbackPercentiles(p0, p100, allDist.percentage, precision),
      };
    }
  }

  // We don't expect to get here, but worst case scenario, return a valid distribution structure
  return {
    ...FALLBACK_DISTRIBUTION,
    metricKey,
    metricKeyNormalised: `fallback_normalised_${metricKey}`,
  };
};

/**
 * Returns metric distribution bucket data with fallback lookup/generation if position-specific data is missing for the
 * given metric
 */
export const getMetricBucket = (
  metricKey: string,
  positionSpecific: FormattedMetricDistributionBucket[],
  allPositionsPercentiles?: FormattedMetricDistributionPercentiles[],
): FormattedMetricDistributionBucket => {
  // Try position-specific first
  const positionBucket = positionSpecific.find(b => b.metricKey === metricKey);
  if (positionBucket) return positionBucket;

  // Fall back to 'all' positions
  if (allPositionsPercentiles) {
    const allPercentile = allPositionsPercentiles.find(p => p.metricKey === metricKey);
    if (allPercentile) {
      const min = allPercentile.percentiles[0].value;
      const max = allPercentile.percentiles[100].value;

      return {
        ...FALLBACK_BUCKET,
        metricKey,
        metricKeyNormalised: allPercentile.metricKeyNormalised,
        percentage: allPercentile.percentage,
        reverseScale: allPercentile.reverseScale,
        min,
        max,
        buckets: [],
        quartiles: {
          p25: allPercentile.percentiles[25].value,
          median: allPercentile.percentiles[50].value,
          p75: allPercentile.percentiles[75].value,
        },
      };
    }
  }

  // We don't expect to get here, but worst case scenario, return a valid bucket structure
  return {
    ...FALLBACK_BUCKET,
    metricKey,
    metricKeyNormalised: `fallback_normalised_${metricKey}`,
  };
};
